import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, Clock, User, Eye, Edit, ExternalLink } from 'lucide-react'
import { templateAPI } from '../../services/api'
import { toUtcDate } from '../../utils/dateFormat'

const TodayTomorrowDesigns = () => {
  const navigate = useNavigate()
  const [todayDesigns, setTodayDesigns] = useState([])
  const [tomorrowDesigns, setTomorrowDesigns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('today')

  useEffect(() => {
    fetchDesignsData()
  }, [])

  const fetchDesignsData = async () => {
    try {
      setLoading(true)
      
      // Fetch today's and tomorrow's designs
      const [todayResponse, tomorrowResponse] = await Promise.all([
        templateAPI.getDesignsByDate({ date: 'today' }),
        templateAPI.getDesignsByDate({ date: 'tomorrow' })
      ])

      if (todayResponse.data.success) {
        setTodayDesigns(todayResponse.data.data)
      }

      if (tomorrowResponse.data.success) {
        setTomorrowDesigns(tomorrowResponse.data.data)
      }
    } catch (err) {
      console.error('Error fetching designs data:', err)
      setError('Failed to load designs data')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenInEditor = (templateId) => {
    navigate(`/editor/${templateId}`)
  }

  const formatTime = (dateString) => {
    const date = toUtcDate(dateString)
    return date.toLocaleTimeString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    })
  }

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in_progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'review': 'bg-purple-100 text-purple-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const renderDesignsTable = (designs) => {
    if (designs.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>No designs scheduled for {activeTab === 'today' ? 'today' : 'tomorrow'}</p>
        </div>
      )
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Design
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assignee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {designs?.map((design) => (
              <tr key={design?.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      {design?.thumbnail ? (
                        <img 
                          className="h-10 w-10 rounded-lg object-cover" 
                          src={design?.thumbnail} 
                          alt={design?.name}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
                          <Eye className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                    </div>
                    {/* <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {design?.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {design?.category || 'General'}
                      </div>
                    </div> */}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center text-sm text-gray-900">
                    <Clock className="w-4 h-4 mr-2 text-gray-400" />
                    {formatTime(design?.scheduled_time || design?.created_at)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(design?.status)}`}>
                    {design?.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center text-sm text-gray-900">
                    <User className="w-4 h-4 mr-2 text-gray-400" />
                    {design?.assignee_name || design?.creator_name || 'Unassigned'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleOpenInEditor(design?.id)}
                      className="text-blue-600 hover:text-blue-900 flex items-center"
                      title="Open in Editor"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => window.open(`/preview/${design?.id}`, '_blank')}
                      className="text-gray-600 hover:text-gray-900 flex items-center"
                      title="Preview"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 text-center">
          <div className="text-red-500 mb-2">⚠️</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchDesignsData}
            className="text-blue-600 hover:text-blue-700"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm">
      {/* Tab Headers */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex">
          <button
            onClick={() => setActiveTab('today')}
            className={`py-4 px-6 text-sm font-medium border-b-2 ${
              activeTab === 'today'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Today ({todayDesigns.length})
          </button>
          <button
            onClick={() => setActiveTab('tomorrow')}
            className={`py-4 px-6 text-sm font-medium border-b-2 ${
              activeTab === 'tomorrow'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Tomorrow ({tomorrowDesigns.length})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'today' ? renderDesignsTable(todayDesigns) : renderDesignsTable(tomorrowDesigns)}
      </div>
    </div>
  )
}

export default TodayTomorrowDesigns
